import React, { useState } from "react";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    Stack,
    Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { LoadingButton } from "@mui/lab";
import FileUploader from "app/widgets/Shared/FileUploader/FileUploader";
import skuServices from "app/services/sku-services";
import { downloadBuffer } from "app/utils/appHelpers";
import { useSnackbar } from "notistack";
import moment from "moment";

const SKUImportModal = ({
    open = false,
    onClose = () => {},
    onImported = () => {},
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [filesToImport, setFilesToImport] = useState([]);
    const [importing, setImporting] = useState(false);
    const [downloadingTemplate, setDownloadingTemplate] = useState(false);
    const [importErrors, setImportErrors] = useState([]);

    const handleClose = () => {
        setFilesToImport([]);
        setImporting(false);
        setImportErrors([]);
        onClose();
    };

    const downloadTemplate = async () => {
        try {
            setDownloadingTemplate(true);
            const data = await skuServices.downloadTemplate();
            downloadBuffer(data, `skus_template_${moment().format("YYYYMMDDHHmmss")}.xlsx`);
        } catch (_error) {
            // Interceptor handles generic message.
        } finally {
            setDownloadingTemplate(false);
        }
    };

    const importFile = async () => {
        if (!filesToImport.length) return;

        try {
            setImporting(true);
            setImportErrors([]);

            const response = await skuServices.importSkus(filesToImport);
            enqueueSnackbar(response.message || "Importación completada", { variant: "success" });
            onImported();
            handleClose();
        } catch (error) {
            const details = error?.response?.data?.details;
            if (Array.isArray(details) && details.length > 0) {
                setImportErrors(details);
            }
        } finally {
            setImporting(false);
        }
    };

    return (
        <Dialog fullWidth open={open} onClose={handleClose}>
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h2" color="primary">Importar SKUs desde Excel</Typography>
                    <LoadingButton
                        loading={downloadingTemplate}
                        variant="contained"
                        color="success"
                        size="small"
                        endIcon={<DownloadOutlinedIcon />}
                        disabled={downloadingTemplate}
                        loadingPosition="end"
                        onClick={downloadTemplate}
                    >
                        {downloadingTemplate ? "Descargando" : "Ejemplo"}
                    </LoadingButton>
                </Stack>
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Puedes repetir filas para el mismo SKU y cargar múltiples códigos retail.
                    La fila de ejemplo del template se ignora automáticamente.
                </Typography>

                <FileUploader
                    acceptFamily="spreadsheet"
                    maxFiles={1}
                    onFileSelection={setFilesToImport}
                />

                {importErrors.length > 0 && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            Errores detectados
                        </Typography>
                        <List disablePadding>
                            {importErrors.map((errorMessage, index) => (
                                <ListItem key={`sku-import-error-${index}`} sx={{ py: 0 }}>
                                    <Typography variant="body2">- {errorMessage}</Typography>
                                </ListItem>
                            ))}
                        </List>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>Cerrar</Button>
                <LoadingButton
                    loading={importing}
                    variant="contained"
                    color="success"
                    size="small"
                    endIcon={<SaveOutlinedIcon />}
                    disabled={!filesToImport.length}
                    loadingPosition="end"
                    onClick={importFile}
                >
                    {importing ? "Importando" : "Importar"}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default SKUImportModal;
